import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import AdminMessageAttachments from '../components/admin/AdminMessageAttachments';
import {
  fetchAdminConversationMessages,
  fetchAdminFaceLivenessReview,
  fetchAdminUserReviewConversation,
  isSuperAdminRole,
  sendAdminUserReviewMessage,
  submitAdminFaceLivenessReviewDecision,
} from '../services/api/admin';
import type {
  AdminFaceLivenessReview,
  AdminFaceLivenessReviewReasonCode,
  AdminReviewConversation,
  AdminReviewMessage,
} from '../types/admin';
import { subscribeToAdminConversation } from '../services/adminMessageSocket';
import { getAdminAuthUser } from '../services/storage/adminFlow';
import { formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

const FACE_PHOTO_KEYS = [
  { key: 'FACE_LIVENESS_STRAIGHT', label: 'Straight' },
  { key: 'FACE_LIVENESS_LEFT', label: 'Left turn' },
  { key: 'FACE_LIVENESS_RIGHT', label: 'Right turn' },
];

const REASON_OPTIONS: Array<{ value: AdminFaceLivenessReviewReasonCode; label: string }> = [
  { value: 'NO_FACE', label: 'No face visible' },
  { value: 'DIFFERENT_PERSON', label: 'Different person' },
  { value: 'SCREEN_PHOTO', label: 'Screen or printed photo' },
  { value: 'POSE_MISMATCH', label: 'Pose mismatch' },
  { value: 'LOW_QUALITY', label: 'Low quality' },
  { value: 'OTHER', label: 'Other' },
];

function pillClass(status: string | null | undefined): string {
  const normalized = status?.toUpperCase();
  if (normalized === 'ACCEPTED' || normalized === 'CLEARED') return 'admin-pill admin-pill-success';
  if (normalized === 'LOCKED' || normalized === 'DISABLED') return 'admin-pill admin-pill-danger';
  if (normalized === 'MESSAGE_SENT') return 'admin-pill admin-pill-warning';
  return 'admin-pill';
}

function boolText(value: boolean | null | undefined, yes: string, no: string): string {
  return value ? yes : no;
}

const AdminFaceLivenessReviewDetailPage: React.FC = () => {
  const { userId = '' } = useParams<{ userId: string }>();
  const adminUser = getAdminAuthUser();
  const isSuperAdmin = isSuperAdminRole(adminUser?.roles);
  const messageListRef = useRef<HTMLDivElement | null>(null);
  const [detail, setDetail] = useState<AdminFaceLivenessReview | null>(null);
  const [conversation, setConversation] = useState<AdminReviewConversation | null>(null);
  const [messages, setMessages] = useState<AdminReviewMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [conversationError, setConversationError] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [messageError, setMessageError] = useState('');
  const [messageSuccess, setMessageSuccess] = useState('');
  const [reasonCode, setReasonCode] = useState<AdminFaceLivenessReviewReasonCode>('NO_FACE');
  const [reviewNote, setReviewNote] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  const conversationThreadKey = conversation?.bookingId || conversation?.conversationId || null;
  const photoEntries = useMemo(
    () =>
      FACE_PHOTO_KEYS.map((photo) => ({
        ...photo,
        attachment: detail?.user.attachments?.[photo.key] ?? null,
      })),
    [detail?.user.attachments],
  );

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError('');
      setConversationError('');
      try {
        const nextDetail = await fetchAdminFaceLivenessReview(userId);
        if (!mounted) return;
        setDetail(nextDetail);
        setReasonCode((nextDetail.reasonCode as AdminFaceLivenessReviewReasonCode) || 'NO_FACE');
        setReviewNote(nextDetail.reviewNote ?? '');

        try {
          const nextConversation = await fetchAdminUserReviewConversation(userId);
          if (!mounted) return;
          setConversation(nextConversation);
          const threadKey = nextConversation.bookingId || nextConversation.conversationId;
          if (threadKey) {
            const nextMessages = await fetchAdminConversationMessages(threadKey);
            if (!mounted) return;
            setMessages(nextMessages);
          }
        } catch (conversationLoadError: any) {
          if (!mounted) return;
          setConversationError(conversationLoadError.message || 'The user review conversation is temporarily unavailable.');
        }
      } catch (loadError: any) {
        if (!mounted) return;
        setError(loadError.message || 'Could not load face liveness review details.');
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (userId) {
      void load();
    }

    return () => {
      mounted = false;
    };
  }, [userId]);

  const refreshConversationMessages = useCallback(async () => {
    if (!conversationThreadKey) return;
    const latestMessages = await fetchAdminConversationMessages(conversationThreadKey);
    setMessages(latestMessages);
    setConversationError('');
  }, [conversationThreadKey]);

  const scrollMessagesToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    window.requestAnimationFrame(() => {
      const element = messageListRef.current;
      if (!element) return;
      element.scrollTo({ top: element.scrollHeight, behavior });
    });
  }, []);

  useEffect(() => {
    if (!messages.length) return;
    scrollMessagesToBottom(messages.length === 1 ? 'auto' : 'smooth');
  }, [messages.at(-1)?.id, messages.length, scrollMessagesToBottom]);

  useEffect(() => {
    const conversationId = conversation?.conversationId;
    if (!conversationId) return undefined;

    let refreshInFlight = false;
    const refreshFromLiveEvent = async () => {
      if (refreshInFlight) return;
      refreshInFlight = true;
      try {
        await refreshConversationMessages();
      } catch {
        setConversationError('Live message refresh is temporarily unavailable.');
      } finally {
        refreshInFlight = false;
      }
    };

    const dispose = subscribeToAdminConversation(conversationId, {
      onMessage: () => {
        void refreshFromLiveEvent();
      },
    });

    return dispose;
  }, [conversation?.conversationId, refreshConversationMessages]);

  useEffect(() => {
    if (!conversationThreadKey) return undefined;

    const interval = window.setInterval(async () => {
      try {
        await refreshConversationMessages();
      } catch {
        setConversationError('Live message refresh is temporarily unavailable.');
      }
    }, 15000);

    return () => window.clearInterval(interval);
  }, [conversationThreadKey, refreshConversationMessages]);

  const handleSendMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!messageDraft.trim()) {
      setMessageError('Write a message before sending.');
      return;
    }

    setMessageLoading(true);
    setMessageError('');
    setMessageSuccess('');
    try {
      const sentMessage = await sendAdminUserReviewMessage({
        userId,
        content: messageDraft.trim(),
      });
      setMessageDraft('');
      setMessageSuccess('Message sent to the user.');
      if (conversationThreadKey) {
        await refreshConversationMessages();
      } else {
        setMessages((current) => [...current, sentMessage]);
      }
    } catch (sendError: any) {
      setMessageError(sendError.message || 'Could not send the review message.');
    } finally {
      setMessageLoading(false);
    }
  };

  const handleDecision = async (status: string) => {
    const normalizedStatus = status.toUpperCase();
    const needsNote = ['MESSAGE_SENT', 'LOCKED', 'DISABLED'].includes(normalizedStatus);
    const needsReason = ['MESSAGE_SENT', 'LOCKED', 'DISABLED'].includes(normalizedStatus);

    if (needsNote && !reviewNote.trim()) {
      setError('Add a review note before saving this decision.');
      return;
    }
    if ((normalizedStatus === 'LOCKED' || normalizedStatus === 'DISABLED') && !isSuperAdmin) {
      setError('Only super admins can lock or disable a user from this review.');
      return;
    }

    setActionLoading(normalizedStatus);
    setError('');
    setActionSuccess('');
    try {
      const updated = await submitAdminFaceLivenessReviewDecision(userId, {
        status: normalizedStatus,
        reasonCode: needsReason ? reasonCode : null,
        reviewNote: reviewNote.trim() || null,
      });
      setDetail(updated);
      setActionSuccess('Review decision saved.');
    } catch (decisionError: any) {
      setError(decisionError.message || 'Could not save the review decision.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <AdminShell
      badge="Face Liveness Detail"
      copy="Inspect the submitted face photos, message the user for corrections, and record the manual review outcome."
      title={detail?.user.name ? `Review ${detail.user.name}` : 'Review customer face photos'}
    >
      <div className="admin-inline-actions" style={{ marginBottom: '18px' }}>
        <Link className="register-secondary-button" to="/admin/face-liveness-reviews">
          Back to face reviews
        </Link>
      </div>

      {error ? <div className="register-inline-error">{error}</div> : null}
      {actionSuccess ? <div className="register-inline-success">{actionSuccess}</div> : null}

      {loading ? (
        <div className="admin-panel">
          <div className="register-loader" />
        </div>
      ) : detail ? (
        <div className="admin-face-review-layout">
          <div className="admin-face-review-main">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Captured photos</h2>
                  <p className="admin-panel-copy">Review the straight, left, and right face-liveness challenge photos together.</p>
                </div>
                <span className={pillClass(detail.status)}>{detail.status || 'PENDING'}</span>
              </div>

              <div className="admin-face-photo-grid">
                {photoEntries.map((photo) => (
                  <article className="admin-face-photo-card" key={photo.key}>
                    <div className="admin-face-photo-header">
                      <strong>{photo.label}</strong>
                      <span>{photo.attachment?.uploadedAt ? formatDateTime(photo.attachment.uploadedAt) : 'Not uploaded'}</span>
                    </div>
                    {photo.attachment?.url ? (
                      <a href={photo.attachment.url} rel="noreferrer" target="_blank">
                        <img alt={`${photo.label} face liveness capture`} src={photo.attachment.url} />
                      </a>
                    ) : (
                      <div className="admin-face-photo-placeholder">No image available</div>
                    )}
                    <small>{photo.attachment?.originalName || photo.attachment?.fileName || photo.key}</small>
                  </article>
                ))}
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Review messaging</h2>
                  <p className="admin-panel-copy">Send a correction message if the face photos are missing, unclear, or not a real person.</p>
                </div>
              </div>
              {conversationError ? <div className="register-inline-error">{conversationError}</div> : null}
              <div className="admin-message-list" ref={messageListRef}>
                {messages.map((message) => (
                  <article
                    className={`admin-message-item${message.senderType?.toUpperCase() === 'ADMIN' ? ' admin-message-item-admin' : ''}`}
                    key={message.id}
                  >
                    <div className="admin-message-meta">
                      {message.senderName || message.senderType || 'Message'} - {formatDateTime(message.createdAtIso || message.createdAt)}
                    </div>
                    <p className="admin-message-content">{message.content || 'No text content'}</p>
                    <AdminMessageAttachments attachments={message.attachments} />
                  </article>
                ))}
                {!messages.length ? (
                  <p className="admin-empty">
                    {conversationError
                      ? 'The review detail is available, but this user conversation could not be opened right now.'
                      : 'No face review messages have been sent to this user yet.'}
                  </p>
                ) : null}
              </div>
              <form className="register-form" onSubmit={handleSendMessage} style={{ marginTop: '14px' }}>
                <label className="register-label" htmlFor="faceReviewMessage">Message to user</label>
                <textarea
                  className="register-input"
                  id="faceReviewMessage"
                  onChange={(event) => {
                    setMessageDraft(event.target.value);
                    setMessageError('');
                    setMessageSuccess('');
                  }}
                  placeholder="Tell the user what must be corrected before their face verification can be accepted."
                  rows={5}
                  value={messageDraft}
                />
                {messageError ? <div className="register-inline-error">{messageError}</div> : null}
                {messageSuccess ? <div className="register-inline-success">{messageSuccess}</div> : null}
                <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                  <button className="register-submit register-submit-inline" disabled={messageLoading} type="submit">
                    {messageLoading ? 'Sending...' : 'Send message'}
                  </button>
                  {conversation ? <span className="admin-muted-note">Conversation: {conversation.conversationId}</span> : null}
                </div>
              </form>
            </section>
          </div>

          <aside className="admin-face-review-side">
            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">User state</h2>
                  <p className="admin-panel-copy">Core profile, verification, and account controls for this review.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card admin-detail-card-span-2">
                  <span>User</span>
                  <strong>{detail.user.name || 'Unnamed user'}</strong>
                  <small>{detail.user.email || 'No email supplied'}</small>
                  <small>{detail.user.phone || 'No phone supplied'}</small>
                </div>
                <div className="admin-detail-card">
                  <span>Face liveness</span>
                  <strong>{boolText(detail.user.faceLiveness, 'Marked complete', 'Not complete')}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Photo set</span>
                  <strong>{boolText(detail.faceLivenessComplete, 'Complete', 'Incomplete')}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Email</span>
                  <strong>{boolText(detail.user.emailVerified, 'Verified', 'Pending')}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Phone</span>
                  <strong>{boolText(detail.user.phoneVerified, 'Verified', 'Pending')}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Account</span>
                  <strong>{detail.user.enabled === false ? 'Disabled' : 'Enabled'}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Lock</span>
                  <strong>{detail.user.accountNonLocked === false ? 'Locked' : 'Unlocked'}</strong>
                </div>
                <div className="admin-detail-card admin-detail-card-span-2">
                  <span>Latest face upload</span>
                  <strong>{detail.latestFacePhotoUploadedAt ? formatDateTime(detail.latestFacePhotoUploadedAt) : 'No upload timestamp'}</strong>
                </div>
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Decision</h2>
                  <p className="admin-panel-copy">Record the manual outcome. Lock and disable are super-admin actions.</p>
                </div>
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="faceReviewReason">Reason</label>
                <select
                  className="register-input"
                  id="faceReviewReason"
                  onChange={(event) => setReasonCode(event.target.value as AdminFaceLivenessReviewReasonCode)}
                  value={reasonCode}
                >
                  {REASON_OPTIONS.map((reason) => (
                    <option key={reason.value} value={reason.value}>
                      {reason.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="register-field">
                <label className="register-label" htmlFor="faceReviewNote">Review note</label>
                <textarea
                  className="register-input"
                  id="faceReviewNote"
                  onChange={(event) => {
                    setReviewNote(event.target.value);
                    setError('');
                    setActionSuccess('');
                  }}
                  placeholder="Record what was reviewed and any action taken."
                  rows={5}
                  value={reviewNote}
                />
              </div>
              <div className="admin-inline-actions" style={{ marginTop: '14px' }}>
                <button
                  className="register-submit register-submit-inline"
                  disabled={Boolean(actionLoading)}
                  onClick={() => void handleDecision('ACCEPTED')}
                  type="button"
                >
                  {actionLoading === 'ACCEPTED' ? 'Saving...' : 'Accept'}
                </button>
                <button
                  className="register-secondary-button"
                  disabled={Boolean(actionLoading)}
                  onClick={() => void handleDecision('MESSAGE_SENT')}
                  type="button"
                >
                  {actionLoading === 'MESSAGE_SENT' ? 'Saving...' : 'Mark messaged'}
                </button>
                <button
                  className="register-secondary-button"
                  disabled={Boolean(actionLoading)}
                  onClick={() => void handleDecision('CLEARED')}
                  type="button"
                >
                  {actionLoading === 'CLEARED' ? 'Saving...' : 'Clear flag'}
                </button>
              </div>
              <div className="admin-inline-actions" style={{ marginTop: '12px' }}>
                <button
                  className="register-secondary-button"
                  disabled={Boolean(actionLoading) || !isSuperAdmin}
                  onClick={() => void handleDecision('LOCKED')}
                  type="button"
                >
                  {actionLoading === 'LOCKED' ? 'Saving...' : 'Lock account'}
                </button>
                <button
                  className="register-secondary-button register-button-danger"
                  disabled={Boolean(actionLoading) || !isSuperAdmin}
                  onClick={() => void handleDecision('DISABLED')}
                  type="button"
                >
                  {actionLoading === 'DISABLED' ? 'Saving...' : 'Disable account'}
                </button>
                {!isSuperAdmin ? <span className="admin-muted-note">Super admin required for lock or disable.</span> : null}
              </div>
            </section>

            <section className="admin-panel">
              <div className="admin-panel-header">
                <div>
                  <h2 className="admin-panel-title">Last review</h2>
                  <p className="admin-panel-copy">Most recent manual face-liveness decision stored by user-management.</p>
                </div>
              </div>
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <span>Status</span>
                  <strong>{detail.status || 'PENDING'}</strong>
                </div>
                <div className="admin-detail-card">
                  <span>Reason</span>
                  <strong>{detail.reasonCode || 'None'}</strong>
                </div>
                <div className="admin-detail-card admin-detail-card-span-2">
                  <span>Reviewer</span>
                  <strong>{detail.reviewedByEmail || 'Not reviewed'}</strong>
                </div>
                <div className="admin-detail-card admin-detail-card-span-2">
                  <span>Reviewed at</span>
                  <strong>{detail.reviewedAt ? formatDateTime(detail.reviewedAt) : 'Not reviewed'}</strong>
                </div>
              </div>
            </section>
          </aside>
        </div>
      ) : (
        <div className="admin-panel">
          <p className="admin-empty">This face liveness review could not be found.</p>
        </div>
      )}
    </AdminShell>
  );
};

export default AdminFaceLivenessReviewDetailPage;
